import React, { useState, useEffect } from 'react';
import { Modal } from '@/components/ui';
import { formatCurrency } from '@/lib/utils';

interface LeaveEarlyModalProps {
  isOpen: boolean;
  onClose: () => void;
  playerName: string;
  cashIn: number;
  initialStack?: number;
  initialCash?: number;
  mode: 'leave' | 'edit';
  onConfirm: (stack: number, cash: number) => Promise<void>;
}

export function LeaveEarlyModal({
  isOpen,
  onClose,
  playerName,
  cashIn,
  initialStack,
  initialCash,
  mode,
  onConfirm,
}: LeaveEarlyModalProps) {
  const [stack, setStack] = useState<string>('');
  const [cash, setCash] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && initialStack !== undefined && initialCash !== undefined) {
      setStack(String(initialStack));
      setCash(String(initialCash));
    } else {
      setStack('');
      setCash('');
    }
  }, [mode, initialStack, initialCash, isOpen]);

  const stackNum = parseFloat(stack) || 0;
  const cashNum = parseFloat(cash) || 0;
  // Cash they're taking that wasn't tracked = cash - cashIn
  const netCashAdjustment = cashNum - cashIn;
  const isValid = stackNum >= 0 && cashNum >= 0;

  const handleConfirm = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    try {
      await onConfirm(stackNum, cashNum);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={mode === 'edit' ? 'EDIT DEPARTURE' : 'PLAYER LEFT EARLY'}>
      <div className="space-y-5 py-2">
        {/* Player Name */}
        <div className="bg-[#f5f7f8] rounded-xl p-3 text-center">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#595c5d] mb-1">Player</div>
          <div className="text-[16px] font-extrabold text-[#2c2f30]">{playerName}</div>
        </div>

        {/* Inputs */}
        <div className="space-y-4">
          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-[#595c5d]">
              Remaining Stack ($)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-[#595c5d] opacity-50">$</span>
              <input
                autoFocus
                type="number"
                step="0.5"
                min="0"
                value={stack}
                onChange={(e) => setStack(e.target.value)}
                placeholder="0.00"
                className="w-full bg-[#f5f7f8] border border-[#e5e9ea] rounded-xl py-3 px-4 pl-7 text-[15px] font-bold text-[#2c2f30] outline-none focus:border-[#006855] transition-colors"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-[#595c5d]">
              Cash They're Leaving With ($)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-[#595c5d] opacity-50">$</span>
              <input
                type="number"
                step="0.5"
                min="0"
                value={cash}
                onChange={(e) => setCash(e.target.value)}
                placeholder="0.00"
                className="w-full bg-[#f5f7f8] border border-[#e5e9ea] rounded-xl py-3 px-4 pl-7 text-[15px] font-bold text-[#2c2f30] outline-none focus:border-[#006855] transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="bg-[#e8f5f0] border border-[#0068551f] rounded-xl p-3 space-y-1.5">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#006855] opacity-70">
            Summary
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-[#595c5d]">Cash already bought in</span>
            <span className="font-bold text-[#2c2f30]">{formatCurrency(cashIn)}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-[#595c5d]">Cash leaving with</span>
            <span className="font-bold text-[#2c2f30]">{formatCurrency(cashNum)}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-[#595c5d]">Net cash adjustment</span>
            <span className={`font-bold ${netCashAdjustment >= 0 ? 'text-[#b31b25]' : 'text-[#006855]'}`}>
              {netCashAdjustment >= 0 ? '+' : '-'}{formatCurrency(Math.abs(netCashAdjustment))}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 pt-1">
          <button
            onClick={handleConfirm}
            disabled={!isValid || submitting}
            className={`w-full py-3 rounded-full text-xs font-extrabold uppercase tracking-wider transition-all ${
              isValid && !submitting
                ? 'bg-[#006855] text-white shadow-lg shadow-[#006855]/30 active:scale-95 cursor-pointer'
                : 'bg-[#e5e9ea] text-[#8a8d8e] cursor-not-allowed'
            }`}
          >
            {submitting ? 'Saving...' : mode === 'edit' ? 'Save Changes' : 'Confirm Departure'}
          </button>
          <button
            onClick={onClose}
            className="w-full py-3 rounded-full text-xs font-extrabold uppercase tracking-wider text-[#595c5d] bg-transparent hover:bg-[#f5f7f8] transition-colors cursor-pointer"
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}
